import React, { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useNotification, usePaginationQueryParams } from '@app/hooks'

import { getCompetitors } from '@app/api/routes/locationCompetitors'
import { postTranslationsConfigCompetitors } from '@app/api/routes/translationsConfig'

import { LOCATION_KEY, TRANSLATIONS_CONFIG_KEY } from '@app/constants/queryKeys'

import { getTranslationsCompetitorsDataFromResponse } from '@app/utils/responseAdapters'
import { sorterDispatcher } from '@app/utils/table'

import { LntButton, LntDrawer } from '@app/components/base'
import { ExtendedTable } from '@app/components'
import { TableSearchButton } from '@app/components/TableSettingsToolbar'

import { topFiltersQuerySchema } from './config'

import { TopFilters } from './TopFilters'

import styles from './styles.module.scss'

import type { Key } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { TranslationsCompetitorsDataSource } from '@app/types/dataSource'
import type { Column } from '@app/components/base/Table/types'
import type { GetCompetitorsParams } from '@app/api/routes/locationCompetitors/types'
import type {
  TranslationsAddCompetitorsDrawerColumns,
  TranslationsAddCompetitorsDrawerProps,
  TranslationsAddCompetitorsDrawerFilters
} from './types'
import type { PaginatedParams } from '@app/types/pagination'

const toolbar = (
  // TODO: Добавить табличный фильтр
  <TableSearchButton key='search-competitors' />
)

const columns: Array<Column<TranslationsAddCompetitorsDrawerColumns>> = [
  {
    key: 'network',
    dataIndex: 'network',
    title: 'Сеть конкурента',
    sorter: (a, b) => sorterDispatcher(a.network, b.network)
  },
  {
    key: 'text',
    dataIndex: 'text',
    title: 'Адрес конкурента',
    sorter: (a, b) => sorterDispatcher(a.text, b.text)
  }
]

export const TranslationsAddCompetitorsDrawer: React.FC<TranslationsAddCompetitorsDrawerProps> = props => {
  const { onClose } = props

  const [selectedIds, setSelectedIds] = useState<Key[]>([])
  const [competitorsFilters, setCompetitorsFilters] = useState<TranslationsAddCompetitorsDrawerFilters>({})

  const queryClient = useQueryClient()
  const notification = useNotification()

  const { resetCurrentPage, setPagination, pagination, paginationParams } = usePaginationQueryParams()

  const competitorsDataParams = useMemo<PaginatedParams<GetCompetitorsParams>>(
    () => ({ ...competitorsFilters, limit: paginationParams.limit, offset: paginationParams.offset }),
    [paginationParams, competitorsFilters]
  )

  const { data: competitorsData, isLoading: isCompetitorsDataLoading } = useQuery({
    queryKey: [LOCATION_KEY.locationCompetitorsItems, competitorsDataParams],
    queryFn: async () => await getCompetitors(competitorsDataParams)
  })

  const postTranslationsConfigCompetitorsMutation = useMutation({
    mutationFn: postTranslationsConfigCompetitors,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_CONFIG_KEY.competitors] })

      notification.success({
        message: 'Выбранные магазины добавлены для транслирования.'
      })

      onClose?.()
    }
  })

  const competitorsDataSource = useMemo(
    () => getTranslationsCompetitorsDataFromResponse(competitorsData),
    [competitorsData]
  )

  const handleFiltersChange = (filters: FiltersParams): void => {
    setCompetitorsFilters(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys)
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleAddTranslationsConfigCompetitors = async (): Promise<void> => {
    const parsedSelectedIds = selectedIds.map(String)

    if (!parsedSelectedIds.length) return

    postTranslationsConfigCompetitorsMutation.mutate({ competitorIds: parsedSelectedIds })
  }

  const footerButtons = (
    <div className={ styles.listCompetitors__drawerFooter }>
      <LntButton
        disabled={ !selectedIds.length }
        onClick={ handleAddTranslationsConfigCompetitors }
      >
        Добавить
      </LntButton>
    </div>
  )

  return (
    <LntDrawer
      { ...props }
      title='Список магазинов конкурентов'
      width={ 1080 }
      footer={ footerButtons }
    >
      <TopFilters
        onSearch={ handleFiltersChange }
        initialFilters={ topFiltersQuerySchema as FiltersParams }
      />

      <ExtendedTable<TranslationsCompetitorsDataSource>
        loading={ isCompetitorsDataLoading }
        columns={ columns as Array<Column<TranslationsCompetitorsDataSource>> }
        dataSource={ competitorsDataSource }
        toolbar={ toolbar }
        rowSelection={ {
          onChange: handleSelectedRowChange,
          selectedRowKeys: selectedIds
        } }
        pagination={ {
          ...pagination,
          total: competitorsData?.count,
          onChange: handlePaginationChange
        } }
      />
    </LntDrawer>
  )
}
